import { createSignal } from "solid-js"
import { getProviderUsage } from "@/transport/providers"
import { readUserSessionToken } from "@/storage/user-session"

export type ProviderUsageSummary = {
  id?: string
  label: string
  used_percent: number
  reset_at?: number
  limit_window_seconds?: number
  plan_type?: string
  source?: string
  status?: "not_reported"
}

export type ProviderUsageOptions = {
  usage?: ProviderUsageSummary
  windows?: ProviderUsageSummary[]
  plan?: string
  source?: string
  machine?: string
  checkedAt?: number
  stale?: boolean
  usageError?: string
  authExpired?: boolean
}

const usageCacheTTL = 45_000
let cachedUsage: Record<string, ProviderUsageOptions> | undefined
let cachedUsageAt = 0
let inflightUsage: Promise<Record<string, ProviderUsageOptions>> | undefined
let usageGeneration = 0
let usageScope = readUserSessionToken()
const [providerUsageRevision, setProviderUsageRevision] = createSignal(0)

export function watchProviderUsageRevision() {
  return providerUsageRevision()
}

function syncUsageScope() {
  const scope = readUserSessionToken()
  if (scope === usageScope) return
  usageScope = scope
  usageGeneration++
  cachedUsage = undefined
  cachedUsageAt = 0
  inflightUsage = undefined
}

export function clearProviderUsageCache() {
  usageScope = readUserSessionToken()
  usageGeneration++
  cachedUsage = undefined
  cachedUsageAt = 0
  inflightUsage = undefined
}

export function invalidateProviderUsage() {
  clearProviderUsageCache()
  setProviderUsageRevision((revision) => revision + 1)
}

export function getCachedProviderUsage() {
  syncUsageScope()
  return cachedUsage
}

export function fetchProviderUsage(options?: { force?: boolean }) {
  syncUsageScope()
  if (!options?.force && cachedUsage && Date.now() - cachedUsageAt < usageCacheTTL) return Promise.resolve(cachedUsage)
  if (!options?.force && inflightUsage) return inflightUsage
  const generation = usageGeneration
  const request = getProviderUsage()
    .then((data) => {
      if (usageGeneration === generation) {
        cachedUsage = data
        cachedUsageAt = Date.now()
      }
      return data
    })
    .finally(() => {
      if (inflightUsage === request) inflightUsage = undefined
    })
  inflightUsage = request
  return request
}
